import React, { useState, useMemo, useCallback } from 'react'
import { Calendar, Panel, Input, Button, Message, Loader } from 'rsuite'
import { makeBetweenFilter, makeFilter } from '@@partner:helpers/crud/filters'
import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'
import SelectAssemblerPicker from '@components/common/commonFormInput/SelectAssemblerPicker'
import { useFetchAllWorkShifts } from '@@partner:graphql/query/assemblerWorkShifts/allWorkShifts'
import { combineDateAndTime, getLastShiftEndTime, getLastShiftStartTime, saveLastShiftEndTime, saveLastShiftStartTime } from '@@partner:helpers/shifts'
import { notifyError, notifyOk, makeGraphQLErrorNotification } from '@@partner:helpers/notification'
import './AssemblingShiftsScheduler.scss'
import { useMutateUpdateWorkshift } from '@@partner:graphql/mutation/workShift/updateWorkshift'
import { WORKSHIFT } from '@hive/status'
import { WorkShift } from '@@partner:types:app'
import { ShopsPicker } from '@components/common/pickers'
import { useMutateDeleteWorkShift } from '@@partner:graphql/mutation/workShift/updateWorkshift'

const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

export default function AssemblingShiftsScheduler () {
  const [ selectedShop, setSelectedShop ] = useState<GQL_ID | null>(null)
  const [ selectedAssemblers, setSelectedAssemblers ] = useState<GQL_ID[]>([])
  const [ startTime, setStartTime ] = useState<string>(getLastShiftStartTime())
  const [ endTime, setEndTime ] = useState<string>(getLastShiftEndTime())
  const [ currentDate, setCurrentDate ] = useState(new Date())
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ timeError, setTimeError ] = useState<string | null>(null)
  const [ hoveredShiftId, setHoveredShiftId ] = useState<GQL_ID | null>(null)
  const [ deletingShiftId, setDeletingShiftId ] = useState<GQL_ID | null>(null)

  const [ updateWorkshift ] = useMutateUpdateWorkshift()
  const [ deleteWorkshift ] = useMutateDeleteWorkShift()

  const hasShopSelected = Boolean(selectedShop)
  const hasSelection = selectedAssemblers.length > 0
  const hasAssemblerSelection = hasShopSelected && hasSelection

  const calendarDateRange = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()

    const firstDayOfMonth = new Date(year, month, 1)
    const firstDayOfWeek = firstDayOfMonth.getDay()
    const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
    const calendarStartDate = new Date(firstDayOfMonth)
    calendarStartDate.setDate(firstDayOfMonth.getDate() - daysToSubtract)

    const lastDayOfMonth = new Date(year, month + 1, 0)
    const lastDayOfWeek = lastDayOfMonth.getDay()
    const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek
    const calendarEndDate = new Date(lastDayOfMonth)
    calendarEndDate.setDate(lastDayOfMonth.getDate() + daysToAdd)

    return {
      startDate: calendarStartDate,
      endDate: calendarEndDate
    }
  }, [ currentDate ])

  const workshiftVariables = useMemo(() => {
    if (!hasAssemblerSelection) {
      return undefined
    }

    const { startDate, endDate } = calendarDateRange
    const startTimestamp = new Date(startDate)
    startTimestamp.setHours(0, 0, 0, 0)

    const endTimestamp = new Date(endDate)
    endTimestamp.setHours(23, 59, 59, 999)

    const filters = [
      makeBetweenFilter('ts_starting', {
        from: startTimestamp.getTime(),
        to: endTimestamp.getTime()
      }),
      makeFilter('assembler_id', 'contains', selectedAssemblers)
    ]

    return {
      filter: { and: filters },
      pager: { page: 1, limit: 1000 },
      sort: {
        direction: 0,
        fieldName: 'ts_starting'
      }
    }
  }, [ hasAssemblerSelection, calendarDateRange, selectedShop, selectedAssemblers ])

  const {
    workshifts: shifts = [],
    loadingWorkShifts,
    errorWorkShifts,
    refetch
  } = useFetchAllWorkShifts(workshiftVariables)

  const shiftsByDate = useMemo(() => {
    const groups: Record<string, WorkShift[]> = {}
    shifts.forEach((shift) => {
      if (!shift.plannedStartAt) {
        return
      }
      const date = new Date(Number(shift.plannedStartAt))
      const key = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
        .getDate()
        .toString()
        .padStart(2, '0')}`
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(shift)
    })
    return groups
  }, [ shifts ])

  const shiftsByAssembler = useMemo(() => {
    const groups: Record<string, WorkShift[]> = {}
    shifts.forEach((shift) => {
      const assemblerId = shift.assembler?.id
      if (!assemblerId) {
        return
      }
      if (!groups[assemblerId]) {
        groups[assemblerId] = []
      }
      groups[assemblerId].push(shift)
    })
    return groups
  }, [ shifts ])

  const resetTimeError = useCallback(() => setTimeError(null), [])

  const handleTimeChange = useCallback(
    (setter: (value: string) => void) => (value: string) => {
      setter(value)
      resetTimeError()
    },
    [ resetTimeError ]
  )

  const handleTimeBlur = useCallback(
    (value: string) => {
      if (value && !timePattern.test(value)) {
        setTimeError('Введите время в формате ЧЧ:ММ')
      }
    },
    []
  )

  const validateForm = useCallback(() => {
    if (!hasShopSelected) {
      notifyError('Выберите магазин')
      return false
    }

    if (!hasAssemblerSelection) {
      notifyError('Выберите хотя бы одного сборщика')
      return false
    }

    if (!timePattern.test(startTime) || !timePattern.test(endTime)) {
      setTimeError('Введите время в формате ЧЧ:ММ')
      return false
    }

    const startDate = combineDateAndTime(new Date(), startTime)
    const endDate = combineDateAndTime(new Date(), endTime)
    if (endDate <= startDate) {
      setTimeError('Время окончания должно быть позже времени начала')
      return false
    }

    return true
  }, [ hasShopSelected, hasAssemblerSelection, startTime, endTime ])

  const handleDateSelect = useCallback(
    async (date: Date) => {
      if (!validateForm()) {
        return
      }

      const plannedStartAt = combineDateAndTime(date, startTime)
      const plannedFinishAt = combineDateAndTime(date, endTime)

      setIsSubmitting(true)
      try {
        const createTasks = selectedAssemblers.map((assemblerId) =>
          updateWorkshift({
            variables: {
              createWorkShift: true,
              workShiftInfoInput: {
                assembler_id: assemblerId,
                plannedStartAt: plannedStartAt.getTime(),
                plannedFinishAt: plannedFinishAt.getTime(),
                status: WORKSHIFT.INITIAL
              }
            }
          })
        )

        await Promise.all(createTasks)

        saveLastShiftStartTime(startTime)
        saveLastShiftEndTime(endTime)

        notifyOk('Смены созданы', `Создано смен: ${selectedAssemblers.length}`)
        await refetch?.()
      }
      catch (error) {
        makeGraphQLErrorNotification('Не удалось создать смены')(error as any)
      }
      finally {
        setIsSubmitting(false)
      }
    },
    [ validateForm, updateWorkshift, selectedAssemblers, startTime, endTime, refetch ]
  )

  const handleDeleteShift = useCallback(
    async (shiftId: GQL_ID) => {
      setDeletingShiftId(shiftId)
      try {
        await deleteWorkshift({ variables: { workshift_id: shiftId } })
        notifyOk('Смена удалена')
        await refetch?.()
      }
      catch (error) {
        makeGraphQLErrorNotification('Не удалось удалить смену')(error as any)
      }
      finally {
        setDeletingShiftId(null)
        setHoveredShiftId(null)
      }
    },
    [ deleteWorkshift, refetch ]
  )

  const renderCell = useCallback(
    (date: Date) => {
      const key = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`

      const dayShifts = shiftsByDate[key] || []

      if (dayShifts.length === 0) {
        return (
          <div className="scheduler-cell empty">
            <span className="scheduler-cell__empty-text">Нет смен</span>
          </div>
        )
      }

      const maxVisible = 4
      const visibleShifts = dayShifts.slice(0, maxVisible)
      const rest = dayShifts.length - visibleShifts.length

      return (
        <div className="scheduler-cell filled">
          {visibleShifts.map((shift) => {
            const assemblerName = shift.assembler
              ? `${shift.assembler.first_name} ${shift.assembler.last_name}`
              : `ID ${shift.assembler_id}`
            const start = new Date(Number(shift.plannedStartAt))
            const finish = new Date(Number(shift.plannedFinishAt))
            const isHovered = hoveredShiftId === shift.id
            const isDeleting = deletingShiftId === shift.id

            return (
              <div
                key={shift.id}
                className={`scheduler-cell__shift${isHovered ? ' is-hovered' : ''}${isDeleting ? ' is-deleting' : ''}`}
                onMouseEnter={() => setHoveredShiftId(shift.id)}
                onMouseLeave={() => {
                  if (!deletingShiftId) {
                    setHoveredShiftId((prev) => (prev === shift.id ? null : prev))
                  }
                }}
                onClick={(event) => {
                  event.stopPropagation()
                  if (!isDeleting) {
                    void handleDeleteShift(shift.id)
                  }
                }}
              >
                <div className="scheduler-cell__shift-content">
                  <span className="scheduler-cell__shift-name">{assemblerName}</span>
                  <span className="scheduler-cell__shift-time">
                    {start.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    {'–'}
                    {finish.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <span className="scheduler-cell__shift-delete">
                  {isDeleting ? 'Удаляем…' : 'Удалить'}
                </span>
              </div>
            )
          })}
          {rest > 0 && (
            <div className="scheduler-cell__more">+{rest}</div>
          )}
        </div>
      )
    },
    [ shiftsByDate, hoveredShiftId, deletingShiftId, handleDeleteShift ]
  )

  return (
    <Panel
      header="Планировщик смен"
      bordered
      className="assembling-shifts-scheduler"
    >
      <div className="scheduler-controls">
        <div className="scheduler-controls__row">
          <div className="scheduler-field">
            <label>Магазин</label>
            <ShopsPicker
              value={selectedShop}
              onChange={(value) => {
                setSelectedShop(value)
                setSelectedAssemblers([])
              }}
              placeholder="Выберите магазин"
              searchable
            />
          </div>

          <div className="scheduler-field">
            <label>Сборщики</label>
            <SelectAssemblerPicker
              isMultiple
              value={selectedAssemblers}
              onChange={(value) => setSelectedAssemblers(value || [])}
              placeholder={hasShopSelected ? 'Выберите сборщиков' : 'Сначала выберите магазин'}
              disabled={!hasShopSelected}
              filter={{
                and: [
                  {
                    fields: [ 'shop_id' ],
                    op: 'eq',
                    value: [ selectedShop! ]
                  },
                  {
                    fields: [ 'status' ],
                    op: 'eq',
                    value: [ 1 ]
                  }
                ]
              }}
            />
          </div>

          <div className="scheduler-field">
            <label>Время смены</label>
            <div className="scheduler-time-range">
              <Input
                value={startTime}
                placeholder="09:00"
                onChange={handleTimeChange(setStartTime)}
                onBlur={() => handleTimeBlur(startTime)}
                maxLength={5}
                disabled={!hasShopSelected}
              />
              <span>—</span>
              <Input
                value={endTime}
                placeholder="18:00"
                onChange={handleTimeChange(setEndTime)}
                onBlur={() => handleTimeBlur(endTime)}
                maxLength={5}
                disabled={!hasShopSelected}
              />
            </div>
          </div>

          <Button
            appearance="subtle"
            disabled={!selectedShop && !selectedAssemblers.length && !startTime && !endTime}
            onClick={() => {
              setSelectedAssemblers([])
              setSelectedShop(null)
              setStartTime(getLastShiftStartTime())
              setEndTime(getLastShiftEndTime())
              setTimeError(null)
            }}
          >
            Сбросить
          </Button>
        </div>

        {!hasShopSelected && (
          <Message type="info" showIcon>
            Выберите магазин, затем сборщиков и задайте время смены, после чего кликните по нужному дню в календаре
          </Message>
        )}

        {hasShopSelected && !hasAssemblerSelection && (
          <Message type="info" showIcon>
            Выберите сборщиков и задайте время смены, затем кликните по нужному дню в календаре
          </Message>
        )}

        {timeError && (
          <Message type="error" showIcon>
            {timeError}
          </Message>
        )}

        { hasShopSelected && hasAssemblerSelection && !timeError && (
          <Message type="info" showIcon>
            Выберите время смены и кликните по нужному дню в календаре
          </Message>
        )}

        {errorWorkShifts && (
          <Message type="error" showIcon>
            Ошибка загрузки расписания: {errorWorkShifts.message}
          </Message>
        )}
      </div>

      {loadingWorkShifts && hasAssemblerSelection && (
        <Loader content="Загрузка расписания..." />
      )}

      <Calendar
        locale={{
          sunday: 'Вс',
          monday: 'Пн',
          tuesday: 'Вт',
          wednesday: 'Ср',
          thursday: 'Чт',
          friday: 'Пт',
          saturday: 'Сб',
          ok: 'ОК',
          today: 'Сегодня',
          yesterday: 'Вчера'
        }}
        renderCell={renderCell}
        onSelect={handleDateSelect}
        onChange={setCurrentDate}
        isoWeek
        disabledDate={() => isSubmitting}
      />
    </Panel>
  )
}
